import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  useTheme,
  Button,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Divider,
  Paper,
  Chip,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-hot-toast";
import { tokens } from "../../theme";
import useAccountantService from "../../api/services/AccountantService";
import useStudentService from "../../api/services/studentService";
import * as image from "../../assets";


const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "0.00";
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return "0.00";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numValue);
};

// Helper function to check if a value is non-zero
const hasValue = (value) => {
  if (value === null || value === undefined || value === "") return false;
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return false;
  return numValue > 0;
};

// Helper function to check if first term has any non-zero values
const hasFirstTermData = (data) => {
  if (!data) return false;
  const totalFees = parseFloat(data.firstTermFee || 0) +
    parseFloat(data.firstTermExamFee || 0) +
    parseFloat(data.firstTermOtherContribution || 0) +
    parseFloat(data.firstTermParentFee || 0);
  const debt = parseFloat(data.firstTermDebt || 0);
  const paidAmount = parseFloat(data.firstTermPaidAmount || 0);
  const totalAmountOwed = totalFees + debt;
  const totalDebt = totalAmountOwed - paidAmount;
  
  return totalFees > 0 || debt > 0 || paidAmount > 0 || totalDebt > 0;
};

// Helper function to check if second term has any non-zero values
const hasSecondTermData = (data) => {
  if (!data) return false;
  const totalFees = parseFloat(data.secondTermFee || 0) +
    parseFloat(data.secondTermExamFee || 0) +
    parseFloat(data.secondTermOtherContribution || 0) +
    parseFloat(data.secondTermParentFee || 0);
  const debt = parseFloat(data.secondTermDebt || 0);
  const paidAmount = parseFloat(data.secondTermPaidAmount || 0);
  const totalAmountOwed = totalFees + debt;
  const totalDebt = totalAmountOwed - paidAmount;
  
  return totalFees > 0 || debt > 0 || paidAmount > 0 || totalDebt > 0;
};

const StudentAccountsData = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { termId, studentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const borderColor = theme.palette.mode === 'dark' ? 'white' : 'black';

  const { getAccountantDataByTermStudent } = useAccountantService();
  const { loadStudentDetails, getParentsByStudentId } = useStudentService();

  const [accountantData, setAccountantData] = useState(null);
  const [studentData, setStudentData] = useState(null);
  const [parentsData, setParentsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [termName, setTermName] = useState(location.state?.termName || '');

  useEffect(() => {
    if (termId && studentId) {
      loadAccountantData();
    }
  }, [termId, studentId]);

  const loadAccountantData = async () => {
    try {
      setLoading(true);
      const data = await getAccountantDataByTermStudent(termId, studentId);
      
      if (!data) {
        toast.error("No account data found for this term and student");
        setAccountantData(null);
        return;
      }
      
      setAccountantData(data);
      
      // Set term name from data if available
      if (data.termName && !termName) {
        setTermName(data.termName);
      }

      // Load student details if studentId is available
      if (data?.studentId || studentId) {
        try {
          const student = await loadStudentDetails(data?.studentId || studentId);
          setStudentData(student);
          
          // Load parents details for this student
          try {
            const parents = await getParentsByStudentId(data?.studentId || studentId);
            setParentsData(Array.isArray(parents) ? parents : []);
          } catch (error) {
            console.error("Error loading parents details:", error);
            setParentsData([]);
          }
        } catch (error) {
          console.error("Error loading student details:", error);
        }
      }
    } catch (error) {
      toast.error("Failed to load account details");
      console.error(error);
      setAccountantData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/student-accounts-terms/${studentId}`);
  };

  const InfoCard = ({ title, value, icon }) => (
    <Card
      sx={{
        height: "100%",
        backgroundColor:
          theme.palette.mode === "dark"
            ? colors.primary[400]
            : "#ffffff",
        border: `1px solid ${
          theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[300]
        }`,
        borderRadius: "8px",
        transition: "transform 0.2s, box-shadow 0.2s",
        boxShadow: theme.palette.mode === "dark" 
          ? "0 2px 8px rgba(0,0,0,0.2)" 
          : "0 2px 8px rgba(0,0,0,0.1)",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: theme.palette.mode === "dark" 
            ? "0 8px 16px rgba(0,0,0,0.3)" 
            : "0 8px 16px rgba(0,0,0,0.15)",
        },
      }}
    >
      <CardContent>
        <Typography
          variant="body2"
          color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[600]}
          sx={{ mb: 1, fontWeight: 500 }}
        >
          {title}
        </Typography>
        <Typography
          variant="h5"
          color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[500]}
          sx={{ fontWeight: 600 }}
        >
          {value}
        </Typography>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="60vh"
      >
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  if (!accountantData) {
    return (
      <Box sx={{ ml: '20px', mr: '20px' }}>
        <Box mb={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mb: 2 }}
          >
            Back
          </Button>
        </Box>
        <Paper
          elevation={3}
          sx={{
            p: 3,
            backgroundColor:
              theme.palette.mode === "dark"
                ? colors.primary[400]
                : "#ffffff",
            borderRadius: "8px",
            textAlign: "center",
          }}
        >
          <Typography variant="h6" color="error">
            No account data found for this term and student
          </Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mt: 2 }}
          >
            Go Back
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ ml: '20px', mr: '20px' }}>
      
      {/* Header Banner */}
      <Box
        height="auto"
        display="flex"
        justifyContent="space-between"
        border={`1px solid ${borderColor}`}
        mb={2}
        flexWrap="wrap"
      >
        {/* Left */}
        <Box
          flex={1}
          display="flex"
          alignItems="center"
          borderRight={{ xs: 'none', md: `1px solid ${borderColor}` }}
          p={1}
          justifyContent="center"
        >
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} alignItems="center">
            <Box
              component="img"
              src={image.visigalogo}
              alt="logo"
              sx={{ height: 80, width: 80, mb: { xs: 1, md: 0 }, mr: { xs: 0, md: 2 } }}
            />
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ fontSize: { xs: '0.7rem', sm: '1.1rem', md: '1.5rem' } }}
            >
              St Mary&apos;s Junior Seminary (VISIGA SEMINARY)
            </Typography>
          </Box>
        </Box>

        {/* Center */}
        <Box
          flex={1}
          display="flex"
          justifyContent="center"
          alignItems="center"
          borderRight={{ xs: 'none', md: `1px solid ${borderColor}` }}
          p={2}
          textAlign="center"
        >
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.4rem' } }}
          >
            MICHANGO & ADA - {termName ? termName.toUpperCase() : 'MUHULA'}
          </Typography>
        </Box>

        {/* Right */}
        <Box
          flex={1}
          display="flex"
          alignItems="center"
          p={1}
          justifyContent="center"
        >
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} alignItems="center">
            <Box
              component="img"
              src={image.cathedral5}
              alt="arch"
              sx={{ height: 80, width: 80, mb: { xs: 1, md: 0 }, mr: { xs: 0, md: 2 } }}
            />
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ fontSize: { xs: '0.7rem', sm: '1.1rem', md: '1.5rem' } }}
            >
              Seminary under Archdiocese of Dar es Salaam
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Student Information Section */}
      {studentData && (
        <Paper
          elevation={3}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor:
              theme.palette.mode === "dark"
                ? colors.primary[400]
                : "#ffffff",
            borderRadius: "8px",
            border: `1px solid ${
              theme.palette.mode === "dark" ? "transparent" : colors.grey[300]
            }`,
          }}
        >
          <Typography
            variant="h5"
            color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[500]}
            sx={{ mb: 2, fontWeight: 600 }}
          >
            Taarifa za mseminari
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography 
                variant="body2" 
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.primary[600]} 
                sx={{ mb: 0.5 }}
              >
                 Jina
              </Typography>
              <Typography 
                variant="body1" 
                color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[500]} 
                sx={{ fontWeight: 500 }}
              >
                {studentData.studentName || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography 
                variant="body2" 
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.primary[600]} 
                sx={{ mb: 0.5 }}
              >
                Darasa
              </Typography>
              <Typography 
                variant="body1" 
                color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[500]} 
                sx={{ fontWeight: 500 }}
              >
                {studentData.className || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography 
                variant="body2" 
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.primary[600]} 
                sx={{ mb: 0.5 }}
              >
                seminarian no:
              </Typography>
              <Typography 
                variant="body1" 
                color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[500]} 
                sx={{ fontWeight: 500 }}
              >
                {studentData.studentNumber || "N/A"}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      

      {/* Financial Details Section */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 3,
          backgroundColor:
            theme.palette.mode === "dark"
              ? colors.primary[400]
              : "#ffffff",
          borderRadius: "8px",
          border: `1px solid ${
            theme.palette.mode === "dark" ? "transparent" : colors.grey[300]
          }`,
        }}
      >
        <Typography
          variant="h5"
          color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[500]}
          sx={{ mb: 3, fontWeight: 600 }}
        >
          Taarifa za Fedha
        </Typography>

        <Grid container spacing={3}>
          {/* First Term Debt */}
          {accountantData && hasValue(accountantData.firstTermDebt) && (
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard
                title="Deni la Muhula uliopita"
                value={`${formatCurrency(accountantData.firstTermDebt)}`}
              />
            </Grid>
          )}

          {/* Second Term Debt */}
          {accountantData && hasValue(accountantData.secondTermDebt) && (
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard
                title="Deni la muhula uliopita"
                value={`${formatCurrency(accountantData.secondTermDebt)}`}
              />
            </Grid>
          )}

          {/* First Term Fees */}
          {accountantData && hasValue(accountantData.firstTermFee) && (
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard
                title="Ada ya muhula wa kwanza"
                value={`${formatCurrency(accountantData.firstTermFee)}`}
              />
            </Grid>
          )}

          {/* Second Term Fees */}
          {accountantData && hasValue(accountantData.secondTermFee) && (
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard
                title="Ada ya muhula wa pili"
                value={`${formatCurrency(accountantData.secondTermFee)}`}
              />
            </Grid>
          )}

          {/* First Term Exam Fee */}
          {accountantData && hasValue(accountantData.firstTermExamFee) && (
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard
                title="Ada ya Mitihani ya Muhula wa Kwanza"
                value={`${formatCurrency(accountantData.firstTermExamFee)}`}
              />
            </Grid>
          )}

          {/* Second Term Exam Fee */}
          {accountantData && hasValue(accountantData.secondTermExamFee) && (
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard
                title="Ada ya Mitihani ya Muhula wa pili"
                value={`${formatCurrency(accountantData.secondTermExamFee)}`}
              />
            </Grid>
          )}

          {/* First Term Other Contribution */}
          {accountantData && hasValue(accountantData.firstTermOtherContribution) && (
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard
                title="Michango Mingine ya Muhula wa Kwanza"
                value={`${formatCurrency(accountantData.firstTermOtherContribution)}`}
              />
            </Grid>
          )}

          {/* Second Term Other Contribution */}
          {accountantData && hasValue(accountantData.secondTermOtherContribution) && (
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard
                title="Michango Mingine ya Muhula wa pili"
                value={`${formatCurrency(accountantData.secondTermOtherContribution)}`}
              />
            </Grid>
          )}

          {/* First Term Parent Fee */}
          {accountantData && hasValue(accountantData.firstTermParentFee) && (
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard
                title="Ada ya Mzazi ya Muhula wa Kwanza"
                value={`${formatCurrency(accountantData.firstTermParentFee)}`}
              />
            </Grid>
          )}

          {/* Second Term Parent Fee */}
          {accountantData && hasValue(accountantData.secondTermParentFee) && (
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard
                title="Ada ya Mzazi ya Muhula wa pili"
                value={`${formatCurrency(accountantData.secondTermParentFee)}`}
              />
            </Grid>
          )}

          {/* First Term Paid Amount */}
          {accountantData && hasValue(accountantData.firstTermPaidAmount) && (
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard
                title="Kiasi Kilicholipwa Muhula wa Kwanza"
                value={`${formatCurrency(accountantData.firstTermPaidAmount)}`}
              />
            </Grid>
          )}

          {/* Second Term Paid Amount */}
          {accountantData && hasValue(accountantData.secondTermPaidAmount) && (
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard
                title="Kiasi Kilicholipwa Muhula wa pili"
                value={`${formatCurrency(accountantData.secondTermPaidAmount)}`}
              />
            </Grid>
          )}
        </Grid>
      </Paper>


      {/* Summary Section */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          backgroundColor:
            theme.palette.mode === "dark"
              ? colors.blueAccent[800]
              : "#ffffff",
          borderRadius: "8px",
          border: `1px solid ${
            theme.palette.mode === "dark" ? "transparent" : colors.grey[300]
          }`,
        }}
      >
        <Typography
          variant="h5"
          color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[500]}
          sx={{ mb: 2, fontWeight: 600 }}
        >
          Summary
        </Typography>
        <Divider 
          sx={{ 
            mb: 2,
            borderColor: theme.palette.mode === "dark" 
              ? colors.grey[700] 
              : colors.grey[300],
          }} 
        />
        <Grid container spacing={2}>
          {/* First Term Section */}
          {accountantData && hasFirstTermData(accountantData) && (
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 2,
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? colors.blueAccent[800]
                    : colors.blueAccent[800],
                borderRadius: "4px",
                border: `1px solid ${
                  theme.palette.mode === "dark" ? "transparent" : colors.blueAccent[600]
                }`,
                mb: 2,
              }}
            >
              <Typography 
                variant="body2" 
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.blueAccent[400]} 
                sx={{ mb: 1 }}
              >
                 Jumla Ya Ada na Michango
              </Typography>
              <Typography 
                variant="body1" 
                color={theme.palette.mode === "dark" ? colors.grey[100] : colors.blueAccent[300]} 
                sx={{ fontWeight: 500, mb: 1 }}
              >
                {formatCurrency(
                  (parseFloat(accountantData.firstTermFee || 0) +
                    parseFloat(accountantData.firstTermExamFee || 0) +
                    parseFloat(accountantData.firstTermOtherContribution || 0) +
                    parseFloat(accountantData.firstTermParentFee || 0)).toString()
                )}
              </Typography>
              <Divider sx={{ my: 1.5, borderColor: theme.palette.mode === "dark" ? colors.grey[700] : "rgba(255,255,255,0.2)" }} />
              <Typography 
                variant="body2" 
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.blueAccent[400]} 
                sx={{ mb: 1 }}
              >
                Deni la Muhula Uliopita 
              </Typography>
              <Typography 
                variant="body1" 
                color={theme.palette.mode === "dark" ? colors.grey[100] : colors.blueAccent[300]} 
                sx={{ fontWeight: 500, mb: 1 }}
              >
                {formatCurrency(accountantData.firstTermDebt || 0)}
              </Typography>
              <Divider sx={{ my: 1.5, borderColor: theme.palette.mode === "dark" ? colors.grey[700] : "rgba(255,255,255,0.2)" }} />
              <Typography 
                variant="body2" 
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.blueAccent[400]} 
                sx={{ mb: 1 }}
              >
                 Jumla Ya deni la {termName ? termName.toUpperCase() : 'MUHULA'}
              </Typography>
              <Typography 
                variant="body1" 
                color={theme.palette.mode === "dark" ? colors.grey[100] : colors.blueAccent[300]} 
                sx={{ fontWeight: 500, mb: 2 }}
              >
                {formatCurrency(
                  ((parseFloat(accountantData.firstTermFee || 0) +
                    parseFloat(accountantData.firstTermExamFee || 0) +
                    parseFloat(accountantData.firstTermOtherContribution || 0) +
                    parseFloat(accountantData.firstTermParentFee || 0)) +
                    parseFloat(accountantData.firstTermDebt || 0)).toString()
                )}
              </Typography>
              <Divider sx={{ my: 1.5, borderColor: theme.palette.mode === "dark" ? colors.grey[700] : "rgba(255,255,255,0.2)" }} />
              <Typography 
                variant="body2" 
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.blueAccent[400]} 
                sx={{ mb: 1 }}
              >
                Kiasi kilicholipwa {termName ? termName.toUpperCase() : 'MUHULA'}
              </Typography>
              <Typography 
                variant="body1" 
                color={theme.palette.mode === "dark" ? colors.grey[100] : colors.blueAccent[300]} 
                sx={{ fontWeight: 500, mb: 2 }}
              >
                {formatCurrency(accountantData.firstTermPaidAmount || 0)}
              </Typography>
              <Divider sx={{ my: 1.5, borderColor: theme.palette.mode === "dark" ? colors.grey[700] : "rgba(255,255,255,0.2)" }} />
              <Typography 
                variant="body2" 
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.blueAccent[400]} 
                sx={{ mb: 1 }}
              >
                Deni la {termName ? termName.toUpperCase() : 'MUHULA'}
              </Typography>
              <Typography 
                variant="h6" 
                color={theme.palette.mode === "dark" ? colors.redAccent[400] : colors.redAccent[500]} 
                sx={{ fontWeight: 600 }}
              >
                {formatCurrency(
                  (((parseFloat(accountantData.firstTermFee || 0) +
                    parseFloat(accountantData.firstTermExamFee || 0) +
                    parseFloat(accountantData.firstTermOtherContribution || 0) +
                    parseFloat(accountantData.firstTermParentFee || 0)) +
                    parseFloat(accountantData.firstTermDebt || 0)) -
                    parseFloat(accountantData.firstTermPaidAmount || 0)).toString()
                )}
              </Typography>
            </Box>
          </Grid>
          )}
          {/* Second Term Section */}
          {accountantData && hasSecondTermData(accountantData) && (
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 2,
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? colors.blueAccent[800]
                    : colors.blueAccent[800],
                borderRadius: "4px",
                border: `1px solid ${
                  theme.palette.mode === "dark" ? "transparent" : colors.blueAccent[600]
                }`,
                mb: 2,
              }}
            >
              <Typography 
                variant="body2" 
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.blueAccent[400]} 
                sx={{ mb: 1 }}
              >
                jumla ya ada ya {termName ? termName.toUpperCase() : 'MUHULA'}
              </Typography>
              <Typography 
                variant="body1" 
                color={theme.palette.mode === "dark" ? colors.grey[100] : colors.blueAccent[300]} 
                sx={{ fontWeight: 500, mb: 1 }}
              >
                {formatCurrency(
                  (parseFloat(accountantData.secondTermFee || 0) +
                    parseFloat(accountantData.secondTermExamFee || 0) +
                    parseFloat(accountantData.secondTermOtherContribution || 0) +
                    parseFloat(accountantData.secondTermParentFee || 0)).toString()
                )}
              </Typography>
              <Divider sx={{ my: 1.5, borderColor: theme.palette.mode === "dark" ? colors.grey[700] : "rgba(255,255,255,0.2)" }} />
              <Typography 
                variant="body2" 
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.blueAccent[400]} 
                sx={{ mb: 1 }}
              >
                Deni la muhula uliopita
              </Typography>
              <Typography 
                variant="body1" 
                color={theme.palette.mode === "dark" ? colors.grey[100] : colors.blueAccent[300]} 
                sx={{ fontWeight: 500, mb: 1 }}
              >
                {formatCurrency(accountantData.secondTermDebt || 0)}
              </Typography>
              <Divider sx={{ my: 1.5, borderColor: theme.palette.mode === "dark" ? colors.grey[700] : "rgba(255,255,255,0.2)" }} />
              <Typography 
                variant="body2" 
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.blueAccent[400]} 
                sx={{ mb: 1 }}
              >
                jumla ya deni la {termName ? termName.toUpperCase() : 'MUHULA'}
              </Typography>
              <Typography 
                variant="body1" 
                color={theme.palette.mode === "dark" ? colors.grey[100] : colors.blueAccent[300]} 
                sx={{ fontWeight: 500, mb: 2 }}
              >
                {formatCurrency(
                  ((parseFloat(accountantData.secondTermFee || 0) +
                    parseFloat(accountantData.secondTermExamFee || 0) +
                    parseFloat(accountantData.secondTermOtherContribution || 0) +
                    parseFloat(accountantData.secondTermParentFee || 0)) +
                    parseFloat(accountantData.secondTermDebt || 0)).toString()
                )}
              </Typography>
              <Divider sx={{ my: 1.5, borderColor: theme.palette.mode === "dark" ? colors.grey[700] : "rgba(255,255,255,0.2)" }} />
              <Typography 
                variant="body2" 
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.blueAccent[400]} 
                sx={{ mb: 1 }}
              >
                kiasi kilicholipwa {termName ? termName.toUpperCase() : 'MUHULA'}
              </Typography>
              <Typography 
                variant="body1" 
                color={theme.palette.mode === "dark" ? colors.grey[100] : colors.blueAccent[300]} 
                sx={{ fontWeight: 500, mb: 2 }}
              >
                {formatCurrency(accountantData.secondTermPaidAmount || 0)}
              </Typography>
              <Divider sx={{ my: 1.5, borderColor: theme.palette.mode === "dark" ? colors.grey[700] : "rgba(255,255,255,0.2)" }} />
              <Typography 
                variant="body2" 
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.blueAccent[400]} 
                sx={{ mb: 1 }}
              >
                Jumla ya deni lilobakia {termName ? termName.toUpperCase() : 'MUHULA'}
              </Typography>
              <Typography 
                variant="h6" 
                color={theme.palette.mode === "dark" ? colors.redAccent[400] : colors.redAccent[500]} 
                sx={{ fontWeight: 600 }}
              >
                {formatCurrency(
                  (((parseFloat(accountantData.secondTermFee || 0) +
                    parseFloat(accountantData.secondTermExamFee || 0) +
                    parseFloat(accountantData.secondTermOtherContribution || 0) +
                    parseFloat(accountantData.secondTermParentFee || 0)) +
                    parseFloat(accountantData.secondTermDebt || 0)) -
                    parseFloat(accountantData.secondTermPaidAmount || 0)).toString()
                )}
              </Typography>
            </Box>
          </Grid>
          )}
        </Grid>
      </Paper>

      {/* Payment Instructions Section */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 3,
          backgroundColor:
            theme.palette.mode === "dark"
              ? colors.greenAccent[800]
              : colors.greenAccent[50],
          borderRadius: "8px",
          border: `1px solid ${
            theme.palette.mode === "dark" ? "transparent" : colors.greenAccent[200]
          }`,
        }}
      >
        <Typography
          variant="h5"
          color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[500]}
          sx={{ mb: 2, fontWeight: 600 }}
        >
          Malipo yafanyike kwenye  Account namba zifuatazo:-
        </Typography>
        <Divider 
          sx={{ 
            mb: 2,
            borderColor: theme.palette.mode === "dark" 
              ? colors.grey[700] 
              : colors.greenAccent[300],
          }} 
        />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Typography 
                variant="body1" 
                color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[600]} 
                sx={{ fontWeight: 600, mb: 1 }}
              >
                CRDB BANK
              </Typography>
              <Typography 
                variant="body2" 
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.primary[500]} 
                sx={{ fontSize: '1.1rem' }}
              >
                0152253103800
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Typography 
                variant="body1" 
                color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[600]} 
                sx={{ fontWeight: 600, mb: 1 }}
              >
                NMB BANK
              </Typography>
              <Typography 
                variant="body2" 
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.primary[500]} 
                sx={{ fontSize: '1.1rem' }}
              >
                21210019782
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Typography 
                variant="body1" 
                color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[600]} 
                sx={{ fontWeight: 600, mb: 1 }}
              >
                MKOMBOZI BANK
              </Typography>
              <Typography 
                variant="body2" 
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.primary[500]} 
                sx={{ fontSize: '1.1rem' }}
              >
                00120600737701
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 2, borderColor: theme.palette.mode === "dark" ? colors.grey[700] : colors.greenAccent[300] }} />
            <Box sx={{ mb: 2 }}>
              <Typography 
                variant="body1" 
                color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[600]} 
                sx={{ fontWeight: 600, mb: 1 }}
              >
                ACCOUNT NAME
              </Typography>
              <Typography 
                variant="body2" 
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.primary[500]} 
                sx={{ fontSize: '1.1rem' }}
              >
                ST. MARY&apos;S JUNIOR SEMINARY – VISIGA
              </Typography>
            </Box>
            <Box>
              <Typography 
                variant="body1" 
                color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[600]} 
                sx={{ fontWeight: 600, mb: 1 }}
              >
                Namba ya simu ya Seminari
              </Typography>
              <Typography 
                variant="body2" 
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.primary[500]} 
                sx={{ fontSize: '1.1rem' }}
              >
                +255 767 927 537 (St. Mary&apos;s Seminary)
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default StudentAccountsData;

